import { TipoComissaoEnum } from '../../../../../shared/enums/pessoa/tipo-comissao.enum';
import { VendedorVendaStruct } from '../../../../../shared/services/structs/vendas/vendedor-venda.struct';
import { percentualDoPreco } from './venda-item.form';

/**
 * O percentual de comissão que um item rende, conforme o cadastro do vendedor.
 *
 * São duas regras, escolhidas pelo `idTipoComissao`:
 *
 *   **Fixa**      - o percentual do vendedor, o mesmo para qualquer item.
 *   **Desconto**  - a faixa em que o desconto do item cai. As faixas são tetos ("desconto até
 *                   X% paga Y% de comissão"), então vale a PRIMEIRA cujo teto alcança o desconto,
 *                   e por isso elas chegam em ordem crescente da API.
 *
 * O desconto da faixa é o desconto EFETIVO do item contra o preço bruto, porque o preço líquido é
 * o valor final de venda do produto e a redução de preço também conta:
 *
 *   - com `precoBrutoUnitario > 0`: (bruto - líquido) / bruto x 100, em 2 casas, nunca negativo
 *     (`percentualDoPreco`). Bruto 100, desconto de 10% (preço 90) e preço editado para 80 dá 20%;
 *     preço acima da lista dá 0;
 *   - sem bruto: o `descontoPercentual` do item.
 *
 * O desconto global do documento, rateado nos itens, fica fora da faixa.
 *
 * Desconto acima de todas as faixas devolve **zero**: a última faixa é o limite do que o cadastro
 * previu, e pagar a comissão dela para um desconto maior premiaria justamente o caso que o
 * vendedor não tinha autorização para dar.
 *
 * Sem vendedor, sem tipo configurado ou sem faixa nenhuma, devolve `null`, que é diferente de
 * zero: quem chama deixa o valor como está, em vez de apagar um percentual digitado à mão.
 */
export function percentualDaComissao(
    vendedor: VendedorVendaStruct | null | undefined,
    precoBrutoUnitario: number | null | undefined,
    precoLiquidoUnitario: number | null | undefined,
    descontoPercentual: number | null | undefined,
): number | null {
    if (!vendedor?.idTipoComissao) return null;

    if (vendedor.idTipoComissao === TipoComissaoEnum.Fixa) {
        return vendedor.percentualComissaoFixa ?? null;
    }

    if (vendedor.idTipoComissao !== TipoComissaoEnum.Desconto) return null;

    const faixas = vendedor.faixas ?? [];

    if (!faixas.length) return null;

    const bruto = Number(precoBrutoUnitario ?? 0) || 0;
    const desconto = bruto > 0
        ? percentualDoPreco(bruto, Number(precoLiquidoUnitario ?? 0) || 0)
        : Number(descontoPercentual ?? 0) || 0;
    const faixa = faixas.find(f => desconto <= Number(f.descontoAtePercentual));

    return faixa ? Number(faixa.comissaoPercentual) : 0;
}
